// Heimatgut instant search — overlay + predictive fetch.
// Server renders results via section_id=heimat-predictive-search.
// No redirect to /search on keystroke — redirect only on explicit submit.

// Analytics hand-off. The search UIs know the query and the result count, the
// PostHog layer (assets/heimat-posthog-events.js) knows whether it may report —
// so pass it along as a DOM event instead of calling posthog from here.
function heimatSearchEvent(name, detail) {
  document.dispatchEvent(new CustomEvent('heimat:search:' + name, { detail: detail }));
}

(function () {
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 180;
  const SECTION_ID = 'heimat-predictive-search';

  function init() {
    const overlay = document.querySelector('[data-heimat-search]');
    if (!overlay) return;

    const input = overlay.querySelector('[data-heimat-search-input]');
    const clearBtn = overlay.querySelector('[data-heimat-search-clear]');
    const resultsEl = overlay.querySelector('[data-heimat-search-results]');
    const loadingEl = overlay.querySelector('[data-heimat-search-loading]');
    const form = overlay.querySelector('[data-heimat-search-form]');
    const openers = document.querySelectorAll('[data-heimat-search-open]');
    const closers = overlay.querySelectorAll('[data-heimat-search-close]');
    const chips = overlay.querySelectorAll('[data-heimat-search-query]');

    if (overlay.parentElement !== document.body) document.body.appendChild(overlay);

    let lastTerm = '';
    let debounceTimer = null;
    let activeController = null;

    function setState(state) {
      overlay.setAttribute('data-state', state);
      loadingEl.hidden = state !== 'loading';
    }

    function open() {
      overlay.setAttribute('data-open', 'true');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('heimat-search-open');
      setTimeout(() => input && input.focus(), 60);
    }

    function close() {
      overlay.setAttribute('data-open', 'false');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('heimat-search-open');
      if (activeController) { activeController.abort(); activeController = null; }
    }

    function reset() {
      input.value = '';
      lastTerm = '';
      resultsEl.innerHTML = '';
      clearBtn.hidden = true;
      overlay.setAttribute('data-has-query', 'false');
      setState('idle');
    }

    function updateClearBtn() {
      clearBtn.hidden = !input.value;
      overlay.setAttribute('data-has-query', input.value ? 'true' : 'false');
    }

    async function runSearch(term) {
      if (activeController) activeController.abort();
      activeController = new AbortController();

      const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=${SECTION_ID}&resources[type]=product`;

      setState('loading');
      try {
        const res = await fetch(url, { signal: activeController.signal });
        if (!res.ok) throw new Error('search failed');
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fragment = doc.querySelector('.shopify-section') || doc.body;
        resultsEl.innerHTML = fragment.innerHTML;
        setState('results');
        heimatSearchEvent('performed', {
          variant: 'overlay',
          term: term,
          results: resultsEl.querySelectorAll('a[href*="/products/"]').length
        });
      } catch (err) {
        if (err.name === 'AbortError') return;
        resultsEl.innerHTML = '';
        setState('idle');
      }
    }

    function onInput() {
      updateClearBtn();
      const term = input.value.trim();
      if (term === lastTerm) return;
      lastTerm = term;

      clearTimeout(debounceTimer);

      if (term.length < MIN_CHARS) {
        resultsEl.innerHTML = '';
        setState('idle');
        return;
      }

      debounceTimer = setTimeout(() => runSearch(term), DEBOUNCE_MS);
    }

    // Capture phase + stopImmediatePropagation blocks third-party apps
    // (e.g. cloud-search-mobile) from intercepting the trigger click.
    openers.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        open();
      }, true);
    });

    closers.forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault();
      close();
    }));

    clearBtn.addEventListener('click', () => {
      reset();
      input.focus();
    });

    input.addEventListener('input', onInput);

    // Results are injected HTML, so listen on the container rather than the links.
    // Product links only: the results markup also holds the "Alle Ergebnisse
    // ansehen" link and, on a search with no hits, a "Bestseller ansehen" CTA.
    // Counting those as a result click would report a click on a search that
    // returned nothing.
    resultsEl.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="/products/"]');
      if (!link) return;
      heimatSearchEvent('result-click', {
        variant: 'overlay',
        term: input.value.trim(),
        url: link.getAttribute('href')
      });
    });

    chips.forEach(chip => chip.addEventListener('click', () => {
      const q = chip.getAttribute('data-heimat-search-query') || '';
      input.value = q;
      input.focus();
      lastTerm = '';
      onInput();
    }));

    form.addEventListener('submit', (e) => {
      if (!input.value.trim()) e.preventDefault();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.getAttribute('data-open') === 'true') {
        close();
      }
      // Keyboard shortcut: Cmd/Ctrl+K opens search
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        overlay.getAttribute('data-open') === 'true' ? close() : open();
      }
    });

    // The mobile header's inline bar does not search in place, it hands over to
    // this overlay. See initInline below.
    document.addEventListener('heimat:search:open-overlay', (e) => {
      const term = (e.detail && e.detail.term) || '';
      if (term) {
        input.value = term;
        updateClearBtn();
        lastTerm = '';
      } else if (input.value) {
        // Handed over from an empty bar: clear whatever the last overlay session
        // left behind, otherwise tapping an empty field opens a full-screen
        // overlay still showing the previous query and its results.
        reset();
      }
      open();
      if (term) onInput();
    });

    setState('idle');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// Inline desktop search — pill input + anchored dropdown, no overlay.
(function () {
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 180;
  const SECTION_ID = 'heimat-predictive-search';

  function initInline() {
    const wrap = document.querySelector('[data-heimat-inline-search]');
    if (!wrap) return;

    const input    = wrap.querySelector('[data-heimat-inline-input]');
    const clearBtn = wrap.querySelector('[data-heimat-inline-clear]');
    const idleEl   = wrap.querySelector('[data-heimat-inline-idle]');
    const loadingEl = wrap.querySelector('[data-heimat-inline-loading]');
    const resultsEl = wrap.querySelector('[data-heimat-inline-results]');

    if (!input) return;

    let lastTerm = '';
    let debounceTimer = null;
    let activeController = null;

    function open() { wrap.setAttribute('data-open', 'true'); }
    function close() {
      wrap.setAttribute('data-open', 'false');
      if (activeController) { activeController.abort(); activeController = null; }
    }

    function showIdle() {
      if (idleEl) idleEl.hidden = false;
      if (resultsEl) resultsEl.innerHTML = '';
      if (loadingEl) loadingEl.hidden = true;
    }

    function updateClear() {
      if (clearBtn) clearBtn.hidden = !input.value;
    }

    async function runSearch(term) {
      if (activeController) activeController.abort();
      activeController = new AbortController();

      if (idleEl) idleEl.hidden = true;
      if (loadingEl) loadingEl.hidden = false;

      const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=${SECTION_ID}&resources[type]=product`;
      try {
        const res = await fetch(url, { signal: activeController.signal });
        if (!res.ok) throw new Error('search failed');
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const frag = doc.querySelector('.shopify-section') || doc.body;
        if (resultsEl) resultsEl.innerHTML = frag.innerHTML;
        if (loadingEl) loadingEl.hidden = true;
        heimatSearchEvent('performed', {
          variant: 'inline',
          term: term,
          results: resultsEl ? resultsEl.querySelectorAll('a[href*="/products/"]').length : 0
        });
      } catch (err) {
        if (err.name === 'AbortError') return;
        if (resultsEl) resultsEl.innerHTML = '';
        showIdle();
      }
    }

    if (resultsEl) {
      // Product links only, same reasoning as the overlay above.
      resultsEl.addEventListener('click', (e) => {
        const link = e.target.closest('a[href*="/products/"]');
        if (!link) return;
        heimatSearchEvent('result-click', {
          variant: 'inline',
          term: input.value.trim(),
          url: link.getAttribute('href')
        });
      });
    }

    // Below 990px the bar is the whole search entry point, and an anchored
    // dropdown under it would leave the visitor typing into a 42px field with the
    // keyboard covering the results. Tapping it opens the full-screen overlay
    // instead; the dropdown stays a desktop thing.
    const overlayEl = document.querySelector('[data-heimat-search]');
    let handedOffAt = 0;

    function isMobile() {
      return window.matchMedia('(max-width: 989px)').matches;
    }

    function handOffToOverlay(e) {
      if (!overlayEl || !isMobile()) return false;
      if (e) e.preventDefault();
      handedOffAt = Date.now();
      close();
      input.blur();
      document.dispatchEvent(new CustomEvent('heimat:search:open-overlay', {
        detail: { term: input.value.trim() }
      }));
      return true;
    }

    // pointerdown, so the overlay is up before the tap can pull the keyboard onto
    // the inline input and flash it shut again.
    wrap.addEventListener('pointerdown', (e) => {
      if (!e.target.closest('[data-heimat-inline-form]')) return;
      if (e.target.closest('[data-heimat-inline-clear]')) return;
      handOffToOverlay(e);
    });

    input.addEventListener('focus', () => {
      // Focus can bounce back here as the overlay takes over; don't hand off twice.
      if (Date.now() - handedOffAt < 500) return;
      if (handOffToOverlay()) return;
      open();
      if (!input.value) showIdle();
    });

    input.addEventListener('input', () => {
      updateClear();
      const term = input.value.trim();
      if (term === lastTerm) return;
      lastTerm = term;
      clearTimeout(debounceTimer);
      if (term.length < MIN_CHARS) { showIdle(); return; }
      debounceTimer = setTimeout(() => runSearch(term), DEBOUNCE_MS);
    });

    if (clearBtn) {
      // Use pointerdown so we win the race against the input's blur handlers
      // and don't get swallowed by other click listeners on the page.
      clearBtn.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        input.value = '';
        lastTerm = '';
        updateClear();
        showIdle();
        close();
        input.blur();
      });
    }

    // Close when clicking outside the wrap, or on the wrap's own padding area.
    document.addEventListener('click', (e) => {
      if (!wrap.contains(e.target) || e.target === wrap) close();
    }, true);

    // Close when focus moves outside (e.g. Tab key).
    document.addEventListener('focusin', (e) => {
      if (!wrap.contains(e.target)) close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && wrap.getAttribute('data-open') === 'true') {
        close();
        input.blur();
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        // Below 990px the overlay owns the shortcut. Focusing the inline input
        // here would hand off to the overlay again and immediately undo the
        // close the overlay's own handler just performed.
        if (isMobile()) return;
        e.preventDefault();
        if (wrap.getAttribute('data-open') === 'true') {
          close(); input.blur();
        } else {
          input.focus();
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInline);
  } else {
    initInline();
  }
})();
